import { useState, useEffect } from "react";
import { Send, CheckCircle2, AlertCircle, RefreshCw, Eye, EyeOff, Save, ExternalLink, AlertTriangle } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * PennylaneWriteConfigCard
 * 
 * Configuration de la clé API Pennylane en lecture/écriture
 * Utilisée par COMPTA FI NEW pour créer des factures, fournisseurs, clients
 */
export default function PennylaneWriteConfigCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/compta-new/write-config/status`);
      if (resp.ok) {
        const data = await resp.json();
        setStatus(data);
      } else {
        setStatus({ connected: false, error: `HTTP ${resp.status}` });
      }
    } catch (e) {
      console.error("Erreur chargement status Pennylane Write:", e);
      setStatus({ connected: false, error: e.message });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const saveToken = async () => {
    if (!newToken.trim()) {
      setMessage({ type: "error", text: "Veuillez entrer un token" });
      return;
    }
    
    setSaving(true);
    setMessage(null);
    try {
      const resp = await authFetch(`${API}/api/compta-new/write-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: newToken.trim() })
      });
      const data = await resp.json();
      
      if (resp.ok && data.success) {
        setMessage({ type: "success", text: "Token Pennylane (Écriture) sauvegardé avec succès !" });
        setNewToken("");
        await loadStatus();
      } else {
        setMessage({ type: "error", text: data.detail || "Erreur lors de la sauvegarde" });
      }
    } catch (e) {
      console.error("Erreur sauvegarde token:", e);
      setMessage({ type: "error", text: "Erreur de connexion au serveur" });
    }
    setSaving(false);
  };

  const maskToken = (token) => {
    if (!token) return "";
    if (token.length < 10) return "••••••••";
    return token.substring(0, 6) + "••••••••" + token.substring(token.length - 4);
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-orange-500/30 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <Send className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">Pennylane (Lecture/Écriture)</h3>
            <p className="text-sm text-slate-400">Pour COMPTA FI NEW - création factures</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading ? (
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          ) : status?.connected ? (
            <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Configuré
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-sm">
              <AlertCircle className="w-4 h-4" />
              Non configuré
            </span>
          )}
        </div>
      </div>
      
      <div className="p-5 space-y-4">
        {/* Avertissement */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-400">Token avec permissions d'écriture</p>
            <p className="text-slate-400 mt-1">
              Ce token sera utilisé pour créer des factures, des fournisseurs et des clients dans Pennylane.
              Assurez-vous qu'il dispose des permissions d'écriture appropriées.
            </p>
          </div>
        </div>
        
        {/* Status actuel */}
        {status?.connected && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-emerald-400">Token d'écriture actif</span>
              <button
                onClick={() => setShowToken(!showToken)}
                className="text-slate-400 hover:text-white"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <code className="text-xs text-slate-300 bg-slate-900/50 px-2 py-1 rounded">
              {showToken ? status.token_preview || "••••••••" : maskToken(status.token_preview)}
            </code>
            {status.last_used && (
              <p className="text-xs text-slate-400 mt-2">
                Dernière utilisation : {new Date(status.last_used).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
        )}
        
        {/* Formulaire nouveau token */}
        <div className="space-y-3">
          <label className="block text-sm text-slate-400">
            {status?.connected ? "Mettre à jour le token d'écriture" : "Entrer le token API Pennylane (Écriture)"}
          </label>
          <div className="flex gap-2">
            <input
              type="password"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              placeholder="Coller le token Pennylane (Écriture) ici..."
              className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm"
            />
            <button
              onClick={saveToken}
              disabled={saving || !newToken.trim()}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg flex items-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder
            </button>
          </div>
        </div>
        
        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg text-sm ${
            message.type === "success" 
              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" 
              : "bg-red-500/20 text-red-400 border border-red-500/30"
          }`}>
            {message.text}
          </div>
        )}
        
        {/* Permissions requises */}
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-xs">
          <p className="font-medium text-orange-400 mb-2">Permissions requises (lecture + écriture) :</p>
          <div className="grid grid-cols-2 gap-1 text-slate-400">
            <span>✅ Factures fournisseurs (R/W)</span>
            <span>✅ Factures clients (R/W)</span>
            <span>✅ Fournisseurs (R/W)</span>
            <span>✅ Clients (R/W)</span>
            <span>✅ Transactions (R)</span>
            <span>✅ Catégories (R)</span>
          </div>
        </div>
        
        {/* Aide */}
        <div className="bg-slate-900/50 rounded-lg p-3 text-xs text-slate-500 space-y-2">
          <p className="font-medium text-slate-400">Comment obtenir le token :</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Connectez-vous à <a href="https://app.pennylane.com" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">Pennylane</a></li>
            <li>Allez dans Paramètres → Développeurs → Générer un token</li>
            <li>Cochez les permissions en <strong>Lecture ET Écriture</strong></li>
            <li>Copiez le token généré et collez-le ci-dessus</li>
          </ol>
          <a 
            href="https://app.pennylane.com/settings/developers/api_tokens" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-orange-400 hover:underline mt-2"
          >
            <ExternalLink className="w-3 h-3" />
            Ouvrir les paramètres API Pennylane
          </a>
        </div>
      </div>
    </div>
  );
}
